import {Stroke} from '../Models/Stroke';
import {Distance} from '../Domain/Distance';
import {Speed} from '../Domain/Speed';
import {Duration} from '../Domain/Duration';
import {Fraction} from '../Domain/Fraction';
import {StrokeRate} from '../Domain/StrokeRate';
import {Power} from '../Domain/Power';
import {HeartRate} from '../Domain/HeartRate';

interface IntermediateSplit {
    distance: Distance;
    duration: Duration;
    strokes: Stroke[];
}

export interface Split {
    distance: Distance;
    duration: Duration;
    speed: Speed;
    power: Power | null;
    strokeRate: StrokeRate | null;
    heartRate: HeartRate | null;
    strokes: Stroke[];
}

function emptySplit(overshoot: Distance = Distance.fromMeters(0)): IntermediateSplit {
    return {
        distance: overshoot,
        duration: Duration.fromSeconds(0),
        strokes: [],
    };
}

export enum Strategy {
    Distance,
    Fraction,
}

interface DistanceSplitStrategy {
    strategy: Strategy.Distance;
    distance: Distance;
}

interface FractionsSplitStrategy {
    strategy: Strategy.Fraction;
    fraction: Fraction;
}

export type SplitStrategy = DistanceSplitStrategy | FractionsSplitStrategy;

function interpolateForStroke(
    split: IntermediateSplit,
    stroke: Stroke,
    targetDistance: Distance,
): IntermediateSplit & {overshoot: Distance} {
    const previousStroke = split.strokes[split.strokes.length - 1];

    const distanceTraveledInStroke = stroke.totalDistance.min(previousStroke.totalDistance);
    const distanceToBeTraveled = targetDistance.min(split.distance);

    const fraction = distanceToBeTraveled.meters / distanceTraveledInStroke.meters;

    const duration =
        stroke.driveTime && previousStroke.driveTime
            ? Duration.fromSeconds(
                  previousStroke.driveTime.seconds +
                      fraction * (stroke.driveTime.seconds - previousStroke.driveTime.seconds),
              ).add(split.duration)
            : Duration.zero();

    return {
        distance: targetDistance,
        duration: duration,
        strokes: split.strokes,
        overshoot: split.distance.min(targetDistance),
    };
}

function applyStroke(split: IntermediateSplit, stroke: Stroke) {
    const strokes = [...split.strokes, stroke];

    //Calculate distance by starting at zero and add the traveled distance in the first stroke
    //Then add the difference in the total distance between the last and first stroke
    let distance = Distance.fromMeters(0);
    if (strokes.length > 0 && strokes[0].distancePerStroke) {
        distance = distance.add(strokes[0].distancePerStroke);
    }
    if (strokes.length > 1 && stroke.totalDistance && strokes[0].totalDistance) {
        distance = distance.add(stroke.totalDistance.min(strokes[0].totalDistance));
    }

    //Same strategy for duration, however just start with the drive time of the first stroke
    //Since the recover of the last one is still part of the previous split
    let duration = Duration.fromSeconds(0);
    if (strokes.length > 0 && strokes[0].driveTime) {
        duration = duration.add(strokes[0].driveTime);
    }
    if (strokes.length > 1) {
        duration = duration.add(stroke.totalTime.subtract(strokes[0].totalTime));
    }

    return {
        distance: stroke.distancePerStroke === null ? split.distance : split.distance.add(stroke.distancePerStroke),
        duration: duration,
        speed: Speed.fromDistanceAndDuration(distance, duration),
        strokes: strokes,
    };
}

function averagePowerFromStrokes(strokes: Stroke[]): Power | null {
    if (strokes.filter((stroke) => stroke.power !== null).length === 0) {
        return null;
    }
    return Power.fromWatts(
        strokes.reduce((cumulativePower: Power, stroke: Stroke) => {
            if (stroke.power === null) {
                return cumulativePower;
            }
            return cumulativePower.add(stroke.power);
        }, Power.fromWatts(0)).watts / strokes.length,
    );
}

function averageStrokeRateFromStrokes(strokes: Stroke[]): StrokeRate | null {
    if (strokes.filter((stroke) => stroke.strokeLength !== null).length === 0) {
        return null;
    }
    return StrokeRate.fromStrokesPerMinute(
        strokes.reduce((cumulativeStrokeRate: number, stroke: Stroke) => {
            if (stroke.strokeRate === null) {
                return cumulativeStrokeRate;
            }
            return cumulativeStrokeRate + stroke.strokeRate.strokesPerMinute;
        }, 0) / strokes.length,
    );
}

function averageHeartRateFromStrokes(strokes: Stroke[]): HeartRate | null {
    if (strokes.length === 0) {
        return null;
    }
    const heartRate = HeartRate.fromBpm(
        strokes.reduce((cumulativeHeartRate: number, stroke: Stroke) => {
            const bpm = stroke.heartRate?.bpm;
            if (!bpm) {
                return cumulativeHeartRate;
            }
            return cumulativeHeartRate + bpm;
        }, 0) / strokes.length,
    );
    return heartRate.bpm > 0 ? heartRate : null;
}

function calculateDistanceSplitTimes(
    strokes: ReadonlyArray<Stroke>,
    requestedSplitDistanceInterval: Distance,
): Split[] {
    const splitDistanceInterval: Distance =
        requestedSplitDistanceInterval.meters < 50 ? Distance.fromMeters(50) : requestedSplitDistanceInterval;

    return strokes
        .filter((stroke) => stroke.speed)
        .reduce((splits: IntermediateSplit[], stroke: Stroke) => {
            if (splits.length === 0) {
                splits.push(emptySplit());
            }

            if (applyStroke(splits[splits.length - 1], stroke).distance.gt(splitDistanceInterval)) {
                const split = interpolateForStroke(splits[splits.length - 1], stroke, splitDistanceInterval);
                splits[splits.length - 1] = split;
                splits.push(emptySplit(split.overshoot));
            }

            splits[splits.length - 1] = applyStroke(splits[splits.length - 1], stroke);

            return splits;
        }, [])
        .filter((split) => {
            return split.duration.seconds > 0 && split.distance.meters >= 5;
        })
        .map((split: IntermediateSplit): Split => {
            return {
                ...split,
                speed: Speed.fromDistanceAndDuration(split.distance, split.duration),
                power: averagePowerFromStrokes(split.strokes),
                strokeRate: averageStrokeRateFromStrokes(split.strokes),
                heartRate: averageHeartRateFromStrokes(split.strokes),
            };
        });
}

export function calculateSplitTimes(strokes: ReadonlyArray<Stroke>, splitStrategy: SplitStrategy): Split[] {
    switch (splitStrategy.strategy) {
        case Strategy.Distance:
            return calculateDistanceSplitTimes(strokes, splitStrategy.distance);
        case Strategy.Fraction:
            const sumDistance = strokes.reduce((sum, stroke) => {
                if (stroke.distancePerStroke === null) {
                    return sum;
                }
                return sum.add(stroke.distancePerStroke);
            }, Distance.fromMeters(0));

            return calculateDistanceSplitTimes(
                strokes,
                Distance.fromMeters(splitStrategy.fraction.value * sumDistance.meters),
            );
    }
}
